/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { AbsoluteCellRange, AbsoluteColumnRange, AbsoluteRowRange } from '../AbsoluteCellRange';
import { CellError, ErrorType, invalidSimpleCellAddress } from '../Cell';
import { ErrorMessage } from '../error-message';
import { Matrix, NotComputedMatrix } from '../Matrix';
// noinspection TypeScriptPreferShortImport
import { AstNodeType } from '../parser/Ast';
import { ArithmeticHelper, coerceScalarToString, fixNegativeZero, isNumberOverflow } from './ArithmeticHelper';
import { CriterionBuilder } from './Criterion';
import { FunctionRegistry } from './FunctionRegistry';
import { cloneNumber, EmptyValue, getRawValue, isExtendedNumber, } from './InterpreterValue';
import { SimpleRangeValue } from './SimpleRangeValue';
export class Interpreter {
    constructor(dependencyGraph, columnSearch, config, stats, dateHelper, numberLiteralsHelper, functionRegistry, namedExpressions, serialization) {
        this.dependencyGraph = dependencyGraph;
        this.columnSearch = columnSearch;
        this.config = config;
        this.stats = stats;
        this.dateHelper = dateHelper;
        this.numberLiteralsHelper = numberLiteralsHelper;
        this.functionRegistry = functionRegistry;
        this.namedExpressions = namedExpressions;
        this.serialization = serialization;
        this.functionRegistry.initializePlugins(this);
        this.arithmeticHelper = new ArithmeticHelper(config, dateHelper, numberLiteralsHelper);
        this.criterionBuilder = new CriterionBuilder(config);
    }
    evaluateAst(ast, formulaAddress) {
        let val = this.evaluateAstWithoutPostprocessing(ast, formulaAddress);
        if (isExtendedNumber(val)) {
            if (isNumberOverflow(getRawValue(val))) {
                return new CellError(ErrorType.NUM, ErrorMessage.NaN);
            }
            else {
                val = cloneNumber(val, fixNegativeZero(getRawValue(val)));
            }
        }
        return wrapperForAddress(val, formulaAddress);
    }
    /**
     * Calculates cell value from formula abstract syntax tree
     *
     * @param formula - abstract syntax tree of formula
     * @param formulaAddress - address of the cell in which formula is located
     */
    evaluateAstWithoutPostprocessing(ast, formulaAddress) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        switch (ast.type) {
            case AstNodeType.EMPTY: {
                return EmptyValue;
            }
            case AstNodeType.CELL_REFERENCE: {
                const address = ast.reference.toSimpleCellAddress(formulaAddress);
                if (invalidSimpleCellAddress(address)) {
                    return new CellError(ErrorType.REF, ErrorMessage.BadRef);
                }
                return this.dependencyGraph.getCellValue(address);
            }
            case AstNodeType.NUMBER:
            case AstNodeType.STRING: {
                return ast.value;
            }
            case AstNodeType.CONCATENATE_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_a = passErrors(leftResult, rightResult)) !== null && _a !== void 0 ? _a : wrapperBinary(this.arithmeticHelper.concat, coerceScalarToString(leftResult), coerceScalarToString(rightResult));
            }
            case AstNodeType.EQUALS_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_b = passErrors(leftResult, rightResult)) !== null && _b !== void 0 ? _b : this.arithmeticHelper.eq(leftResult, rightResult);
            }
            case AstNodeType.NOT_EQUAL_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_c = passErrors(leftResult, rightResult)) !== null && _c !== void 0 ? _c : this.arithmeticHelper.neq(leftResult, rightResult);
            }
            case AstNodeType.GREATER_THAN_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_d = passErrors(leftResult, rightResult)) !== null && _d !== void 0 ? _d : this.arithmeticHelper.gt(leftResult, rightResult);
            }
            case AstNodeType.LESS_THAN_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_e = passErrors(leftResult, rightResult)) !== null && _e !== void 0 ? _e : this.arithmeticHelper.lt(leftResult, rightResult);
            }
            case AstNodeType.GREATER_THAN_OR_EQUAL_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_f = passErrors(leftResult, rightResult)) !== null && _f !== void 0 ? _f : this.arithmeticHelper.geq(leftResult, rightResult);
            }
            case AstNodeType.LESS_THAN_OR_EQUAL_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_g = passErrors(leftResult, rightResult)) !== null && _g !== void 0 ? _g : this.arithmeticHelper.leq(leftResult, rightResult);
            }
            case AstNodeType.PLUS_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_h = passErrors(leftResult, rightResult)) !== null && _h !== void 0 ? _h : wrapperBinary(this.arithmeticHelper.addWithEpsilon, this.arithmeticHelper.coerceScalarToNumberOrError(leftResult), this.arithmeticHelper.coerceScalarToNumberOrError(rightResult));
            }
            case AstNodeType.MINUS_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_j = passErrors(leftResult, rightResult)) !== null && _j !== void 0 ? _j : wrapperBinary(this.arithmeticHelper.subtract, this.arithmeticHelper.coerceScalarToNumberOrError(leftResult), this.arithmeticHelper.coerceScalarToNumberOrError(rightResult));
            }
            case AstNodeType.TIMES_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_k = passErrors(leftResult, rightResult)) !== null && _k !== void 0 ? _k : wrapperBinary(this.arithmeticHelper.multiply, this.arithmeticHelper.coerceScalarToNumberOrError(leftResult), this.arithmeticHelper.coerceScalarToNumberOrError(rightResult));
            }
            case AstNodeType.POWER_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_l = passErrors(leftResult, rightResult)) !== null && _l !== void 0 ? _l : wrapperBinary(this.arithmeticHelper.pow, this.arithmeticHelper.coerceScalarToNumberOrError(leftResult), this.arithmeticHelper.coerceScalarToNumberOrError(rightResult));
            }
            case AstNodeType.DIV_OP: {
                const leftResult = this.evaluateAst(ast.left, formulaAddress);
                const rightResult = this.evaluateAst(ast.right, formulaAddress);
                return (_m = passErrors(leftResult, rightResult)) !== null && _m !== void 0 ? _m : wrapperBinary(this.arithmeticHelper.divide, this.arithmeticHelper.coerceScalarToNumberOrError(leftResult), this.arithmeticHelper.coerceScalarToNumberOrError(rightResult));
            }
            case AstNodeType.PLUS_UNARY_OP: {
                const result = this.evaluateAst(ast.value, formulaAddress);
                if (result instanceof SimpleRangeValue) {
                    return new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected);
                }
                else if (isExtendedNumber(result)) {
                    return this.arithmeticHelper.unaryPlus(result);
                }
                else {
                    return result;
                }
            }
            case AstNodeType.MINUS_UNARY_OP: {
                const result = this.evaluateAst(ast.value, formulaAddress);
                if (result instanceof SimpleRangeValue) {
                    return new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected);
                }
                else {
                    return wrapperUnary(this.arithmeticHelper.unaryMinus, this.arithmeticHelper.coerceScalarToNumberOrError(result));
                }
            }
            case AstNodeType.PERCENT_OP: {
                const result = this.evaluateAst(ast.value, formulaAddress);
                if (result instanceof SimpleRangeValue) {
                    return new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected);
                }
                else {
                    return wrapperUnary(this.arithmeticHelper.unaryPercent, this.arithmeticHelper.coerceScalarToNumberOrError(result));
                }
            }
            case AstNodeType.FUNCTION_CALL: {
                if (this.config.licenseKeyValidityState !== "valid" /* VALID */ && !FunctionRegistry.functionIsProtected(ast.procedureName)) {
                    return new CellError(ErrorType.LIC, ErrorMessage.LicenseKey(this.config.licenseKeyValidityState));
                }
                const pluginEntry = this.functionRegistry.getFunction(ast.procedureName);
                if (pluginEntry && this.config.translationPackage.isFunctionTranslated(ast.procedureName)) {
                    const [pluginFunction, pluginInstance] = pluginEntry;
                    return pluginInstance[pluginFunction](ast, formulaAddress);
                }
                else {
                    return new CellError(ErrorType.NAME, ErrorMessage.FunctionName(ast.procedureName));
                }
            }
            case AstNodeType.NAMED_EXPRESSION: {
                const namedExpression = this.namedExpressions.nearestNamedExpression(ast.expressionName, formulaAddress.sheet);
                if (namedExpression) {
                    return this.dependencyGraph.getCellValue(namedExpression.address);
                }
                else {
                    return new CellError(ErrorType.NAME, ErrorMessage.NamedExpressionName(ast.expressionName));
                }
            }
            case AstNodeType.CELL_RANGE: {
                if (!this.rangeSpansOneSheet(ast)) {
                    return new CellError(ErrorType.REF, ErrorMessage.RangeManySheets);
                }
                const range = AbsoluteCellRange.fromCellRange(ast, formulaAddress);
                const matrixVertex = this.dependencyGraph.getMatrix(range);
                if (matrixVertex) {
                    const matrix = matrixVertex.matrix;
                    if (matrix instanceof NotComputedMatrix) {
                        throw new Error('Matrix should be already computed');
                    }
                    else if (matrix instanceof CellError) {
                        return matrix;
                    }
                    else if (matrix instanceof Matrix) {
                        return SimpleRangeValue.onlyNumbersDataWithRange(matrix.raw(), matrix.size, range);
                    }
                    else {
                        throw new Error('Unknown matrix');
                    }
                }
                else {
                    return SimpleRangeValue.onlyRange(range, this.dependencyGraph);
                }
            }
            case AstNodeType.COLUMN_RANGE: {
                if (!this.rangeSpansOneSheet(ast)) {
                    return new CellError(ErrorType.REF, ErrorMessage.RangeManySheets);
                }
                const range = AbsoluteColumnRange.fromColumnRange(ast, formulaAddress);
                return SimpleRangeValue.onlyRange(range, this.dependencyGraph);
            }
            case AstNodeType.ROW_RANGE: {
                if (!this.rangeSpansOneSheet(ast)) {
                    return new CellError(ErrorType.REF, ErrorMessage.RangeManySheets);
                }
                const range = AbsoluteRowRange.fromRowRange(ast, formulaAddress);
                return SimpleRangeValue.onlyRange(range, this.dependencyGraph);
            }
            case AstNodeType.PARENTHESIS: {
                return this.evaluateAst(ast.expression, formulaAddress);
            }
            case AstNodeType.ERROR_WITH_RAW_INPUT:
            case AstNodeType.ERROR: {
                return ast.error;
            }
        }
    }
    getGpuInstance() {
        const mode = this.config.gpuMode;
        const gpujs = this.config.gpujs;
        if (gpujs === undefined) {
            throw Error('Cannot instantiate GPU.js. Constructor not provided.');
        }
        if (!this.gpu) {
            this.gpu = new gpujs({ mode });
        }
        return this.gpu;
    }
    destroy() {
        if (this.gpu) {
            this.gpu.destroy();
        }
    }
    rangeSpansOneSheet(ast) {
        return ast.start.sheet === ast.end.sheet;
    }
}
function passErrors(left, right) {
    if (left instanceof CellError) {
        return left;
    }
    else if (left instanceof SimpleRangeValue) {
        return new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected);
    }
    else if (right instanceof CellError) {
        return right;
    }
    else if (right instanceof SimpleRangeValue) {
        return new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected);
    }
    else {
        return undefined;
    }
}
function wrapperUnary(op, a) {
    if (a instanceof CellError) {
        return a;
    }
    else {
        return op(a);
    }
}
function wrapperBinary(op, a, b) {
    if (a instanceof CellError) {
        return a;
    }
    else if (b instanceof CellError) {
        return b;
    }
    else {
        return op(a, b);
    }
}
function wrapperForAddress(val, adr) {
    if (val instanceof CellError) {
        return val.attachAddress(adr);
    }
    return val;
}
